import * as React from 'react'
import { useRef, useImperativeHandle } from 'react'

import { Button, Text, Input, Flex, Label } from '@buffer-mono/popcorn'

import { MaxCount, MaxCountText } from './style'

import styles from './TagInput.module.css'

/**
 * Remove unnecessary characters from the input.
 * - @ sign
 * - all whitespace
 * @param {String} value
 */
const filterInput = (value: string): string =>
  value.replace(/@/g, '').replace(/\s/g, '')

interface TagInputProps {
  translations: {
    placeholder?: string
    inputLabel?: string
    inputBtnAddTag?: string
    inputBtnCancel?: string
    maxLimitText?: string
    usernameError?: string
  }
  reachedMaxLimit: boolean
  inputValue: string
  setInputValue: (value: string) => void
  cancel: (e: any) => void
  addTag: (e: any) => void
  disabled: boolean
  inputDisabled: boolean
  error: boolean
}

const TagInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  TagInputProps
>(
  (
    {
      translations,
      inputValue,
      setInputValue,
      disabled,
      inputDisabled,
      cancel,
      addTag,
      reachedMaxLimit,
      error,
    },
    ref,
  ) => {
    /**
     * This strange looking `ref / useImperativeHandle` stuff just makes it so that the
     * parent component can get a ref on this TagInput component and call `ref.current.focus()` on it.
     * https://reactjs.org/docs/hooks-reference.html#useimperativehandle
     */
    const inputEl = useRef(null)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error TS(2740) Type '{ focus: () => void; blur: () => void; }' is missing the following properties from type 'HTMLInputElement': accept, align, alt, autocomplete, and 360 more.
    useImperativeHandle(ref, () => ({
      focus: (): void => {
        if (inputEl.current) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error TS(2339) FIXME: Property 'focus' does not exist on type 'never'.
          inputEl.current.focus()
        }
      },
      blur: (): void => {
        if (inputEl.current) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error TS(2339) FIXME: Property 'blur' does not exist on type 'never'.
          inputEl.current.blur()
        }
      },
    }))

    if (reachedMaxLimit) {
      return (
        <MaxCount>
          <MaxCountText>
            <Text>{translations.maxLimitText}</Text>
          </MaxCountText>
          <Button variant="secondary" size="small" onClick={cancel}>
            {translations.inputBtnCancel}
          </Button>
        </MaxCount>
      )
    }
    return (
      <Flex direction="column" gap="xs">
        <Label>{translations.inputLabel}</Label>
        <Input
          type="input"
          onChange={(e) => {
            setInputValue(filterInput(e.target.value))
          }}
          value={inputValue}
          name="tagInput"
          prefix="@"
          ref={inputEl}
          disabled={inputDisabled}
          className={styles.input}
        />
        {error && (
          <Text color="critical" size="sm">
            {translations.usernameError}
          </Text>
        )}
        <Flex direction="row-reverse" gap="xs" justify="start" fullWidth>
          <Button
            variant="primary"
            size="small"
            onClick={addTag}
            disabled={disabled}
          >
            {translations.inputBtnAddTag}
          </Button>
          <Button variant="tertiary" size="small" onClick={cancel}>
            {translations.inputBtnCancel}
          </Button>
        </Flex>
      </Flex>
    )
  },
)

TagInput.displayName = 'TagInput'

export default TagInput
